import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import {
  SearchableDropdownSize,
  SearchableDropdownState,
} from "./searchableDropdown.types";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

const DEFAULT_MAX_HEIGHT = 260;
const COMPACT_INPUT_HEIGHT_PX = 40;
const COMPACT_INPUT_WIDTH_PX = 148;

export const Container = styled.div<{
  $size: SearchableDropdownSize;
  $fullWidth: boolean;
}>`
  position: relative;
  width: ${(props) =>
    props.$fullWidth
      ? "100%"
      : props.$size === "compact"
        ? `${COMPACT_INPUT_WIDTH_PX}px`
        : `${V2_INPUT_WIDTH_PX}px`};
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

export const FieldWrapper = styled.div<{
  $state: SearchableDropdownState;
  $size: SearchableDropdownSize;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_HEIGHT_PX}px`
      : `${V2_INPUT_HEIGHT_PX}px`};
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
      cursor: not-allowed;
    `}
`;

export const Input = styled.input<{
  $state: SearchableDropdownState;
  $size: SearchableDropdownSize;
}>`
  ${TypographyV2.b2}
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}
`;

export const LeadingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SelectionBadge = styled.span`
  ${TypographyV2.s1}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${ColorV2.Blue7};
  color: ${ColorV2.Blue4};
  white-space: nowrap;
`;

export const TooltipList = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 6px;
`;

export const TooltipColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TooltipItem = styled.span`
  ${TypographyV2.s1}
  color: inherit;
  white-space: nowrap;
`;

export const TooltipAligner = styled.span`
  display: inline-flex;
  align-self: center;
`;

export const IconWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

export const ActionSlot = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

export const List = styled.ul<{ $isOpen?: boolean; $zIndex: number }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0 0;
  padding: 8px 0;
  list-style: none;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  max-height: ${DEFAULT_MAX_HEIGHT}px;
  overflow: hidden;
  z-index: ${(props) => props.$zIndex};
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

export const ListScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: ${DEFAULT_MAX_HEIGHT}px;
  overflow-y: scroll;
  padding: 16px 12px 12px;
  box-sizing: border-box;

  scrollbar-gutter: stable both-edges;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

export const Option = styled.li<{
  $isSelected?: boolean;
  $isHighlighted?: boolean;
  $isMultiSelect?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${ColorV2.Blue6};
    color: ${ColorV2.Blue1};
  }

  ${(props) =>
    props.$isHighlighted &&
    !props.$isSelected &&
    css`
      background-color: ${ColorV2.Blue6};
      color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$isSelected &&
    !props.$isMultiSelect &&
    css`
      background-color: ${ColorV2.Blue1};
      color: ${ColorV2.White};
    `}
`;

export const OptionLabel = styled.span`
  ${TypographyV2.b2}
`;

export const OptionRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

export const OptionRightText = styled.span`
  ${TypographyV2.s1}
  color: inherit;
  opacity: 0.7;
`;

export const OptionCheckbox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const Message = styled.span<{ $state: SearchableDropdownState }>`
  ${TypographyV2.s1}
  color: ${(props) =>
    props.$state === "error" ? ColorV2.NegativeRed : ColorV2.Blue4};
`;

export const EmptyState = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  padding: 10px 12px 16px;
`;
